import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { getShowTimesheetSigningPrompt } from "../../../../services/api/timekeeping";

import { Button } from "../../../../components/ui";

/**
 * WorkerScheduleReminder Component
 *
 * Displays a notification banner reminding users to sign their weekly timesheet.
 *
 * Features:
 * - Shows a prominent notification when weekly timesheet is available for signature
 * - Provides a checkbox confirmation before enabling the sign button
 * - Provides a button to navigate directly to the timesheet signing page
 * - Visual styling to draw attention to the action needed
 *
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes to apply to the component
 * @returns {JSX.Element} A notification banner with a call-to-action button
 */
const WorkerScheduleReminder = ({ className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    /**
     * Ask the API whether the timesheet-signing prompt should be shown.
     *
     * @returns {Promise<void>}
     */
    const checkVisibility = async () => {
      try {
        const visible = await getShowTimesheetSigningPrompt();
        setIsVisible(visible.status);
      } catch (error) {
        console.error("Error checking timesheet visibility:", error);
        setIsVisible(false);
      }
    };

    checkVisibility();
  }, []);

  if (!isVisible) {
    return null; // Don't render if not visible
  }

  // Determine which week to reference based on current day
  /**
   * `"this week"` on Friday/Saturday; `"last week"` on other days.
   *
   * @returns {"this week"|"last week"}
   */
  const getWeekText = () => {
    const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    return today === 5 || today === 6 ? "this week" : "last week"; // Friday = 5, Saturday = 6
  };

  const weekText = getWeekText();

  return (
    <div className={`rounded-md overflow-hidden ${className}`}>
      {/* Notification banner */}
      <div className="bg-green-100 p-4 border-b border-green-200 rounded-t-md">
        <p className="text-center text-gray-800 font-medium">
          Weekly timesheet now available for{" "}
          <span className="text-red-700 font-semibold">{weekText}</span>
        </p>
      </div>
      {/* Confirmation checkbox */}
      <div className="bg-green-50 p-4 border-b border-green-200 flex justify-center">
        <label className="flex items-center cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              className="sr-only"
            />
            <div
              className={`w-6 h-6 border-2 rounded flex items-center justify-center ${
                isConfirmed
                  ? "bg-blue-500 border-blue-500"
                  : "bg-white border-gray-300"
              }`}
            >
              {isConfirmed && (
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          </div>
          <span className="ml-3 text-gray-800 font-medium">
            I have completed all my shifts for{" "}
            <span className="text-red-700 font-semibold">{weekText}</span>.
          </span>
        </label>
      </div>
      {/* Action button */}
      <Button
        onClick={() => navigate("/timekeeping/worker-timesheet")}
        variant="custom"
        text="Sign Weekly Timesheet"
        disabled={!isConfirmed}
        className={`w-full py-3 font-medium rounded-t-none transition-colors ${
          isConfirmed
            ? "bg-red-900 hover:bg-red-800 text-white cursor-pointer"
            : "bg-gray-400 text-gray-200 cursor-not-allowed"
        }`}
      />
    </div>
  );
};

export default WorkerScheduleReminder;
